import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { MIcon } from '../../@material-extend';
import PictureThumbnail from './PictureThumbnail';
import { getFileIconByExtension } from '../../../utils/getFileIconByExtension';

/**
 * A component to display a file thumbnail, either as an image or an icon.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.alt=''] - Alternative text, if the picture can't be displayed.
 * @param {string} [props.ext=''] - File extension.
 * @param {boolean} [props.showThumbnails=false] - Whether to show thumbnails for images.
 * @param {string} [props.size='medium'] - Size of the thumbnail, can be 'small' or 'medium'.
 * @param {string} [props.src=''] - The source URL for the image.
 * @param {string} [props.variant='neutral'] - Style variant for the icon.
 * @param {Object} ref - Ref for forwarding.
 * @returns {JSX.Element} A Box component rendering either an image or an icon.
 */
const FileThumbnail = forwardRef((props, ref) => {
  const { alt = 'file', ext = '', showThumbnails = false, size = 'medium', src = '', variant = 'neutral' } = props;
  const isPicture = !!src && ['png', 'jpg', 'jpeg', 'gif'].includes(ext.toLowerCase());
  const iconName = getFileIconByExtension(ext) || '';
  const dimensions = size === 'small' ? 32 : 40;

  return (
    <Box ref={ref} sx={styleWrapper(dimensions)}>
      {isPicture && showThumbnails ? (
        <PictureThumbnail src={src} alt={alt} ext={ext} dimensions={dimensions} />
      ) : (
        <MIcon
          id={`file-thumbnail-${ext}-icn-${iconName}`}
          icon={iconName}
          variant={variant}
          sx={styleIcon(showThumbnails, dimensions)}
        />
      )}
    </Box>
  );
});

const styleWrapper = (dimensions) => ({
  display: 'grid',
  placeItems: 'center',
  height: dimensions,
  width: dimensions
});

const styleIcon = (showThumbnails, largeSize) => ({
  height: showThumbnails ? largeSize : null,
  width: showThumbnails ? largeSize : null
});

FileThumbnail.propTypes = {
  alt: PropTypes.string,
  ext: PropTypes.string,
  showThumbnails: PropTypes.bool,
  size: PropTypes.string,
  src: PropTypes.string,
  variant: PropTypes.string
};

export default FileThumbnail;
